import React, { useState, type ChangeEvent } from 'react'
import { Controller, type FieldValues, useFormContext, useFormState } from 'react-hook-form'
import InputLabel from '@mui/material/InputLabel'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Box from '@mui/material/Box'
import { grey } from '../../../colors'
import { Autocomplete, Chip, Typography } from '@mui/material'

interface FreeSoloMultiChipsProps {
  name: string
  label?: string
  placeholder?: string
  textFieldProps?: TextFieldProps
  type?: string
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
  size?: 'small' | 'medium'
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  showAdornment?: boolean
  width?: string
}

const FreeSoloMultiChips: React.FC<FreeSoloMultiChipsProps> = ({
  name,
  label,
  textFieldProps,
  placeholder,
  type = 'text',
  disabled = false,
  required,
  size = 'medium',
  fullWidth = true,
  onChange,
  showAdornment = false,
  width,
  ...rest
}) => {
  const [isTyping, setIsTyping] = useState(false)
  const handleInputChange = (_: any, value: string): void => {
    setIsTyping(value.trim().length > 0)
  }

  const { control } = useFormContext<FieldValues>()
  const { errors } = useFormState({ control, name })
  const errorMessage = errors?.[name]?.message ?? null

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box>
          {label !== null && (
            <InputLabel
              required={required}
              sx={{ marginBottom: 2, color: grey[950], fontSize: '14px' }}
            >
              {label}
            </InputLabel>
          )}
          <Autocomplete
            multiple
            freeSolo
            filterOptions={(options) => options}
            value={field.value || []}
            onChange={(_, newValue) => { field.onChange(newValue) }}
            onInputChange={handleInputChange}
            options={[]}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => {
                const { key, ...tagProps } = getTagProps({ index })
                return <Chip variant="filled" label={option} key={key} {...tagProps} />
              })
            }
            renderInput={(params) => {
              const hasChips = (field.value && field.value.length > 0) || false
              return (
                <>
                  <TextField
                    {...params}
                    error={errors[name] != null && true}
                    helperText={errorMessage as string}
                    size={size}
                    placeholder={placeholder}
                    {...textFieldProps}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: hasChips ? 0.6 : 3
                      }
                    }}
                  />
                  <Typography fontSize={'14px'} mt={1.5} color={grey[850]} visibility={isTyping ? 'visible' : 'hidden'}>{'Press Enter to create a new value'}</Typography>
                </>

              )
            }}
          />
        </Box>
      )}
    />
  )
}

export default FreeSoloMultiChips
